/** Aligned with API `ClientServicePreferences` (GAP-F2-065). */

export const CLIENT_PREF_MAX_SHORT = 120;
export const CLIENT_PREF_MAX_NOTE = 500;

export type ClientServicePreferences = {
  preferred_professional_id?: number | null;
  haircut_style?: string | null;
  guard?: string | null;
  beard_notes?: string | null;
  post_care_notes?: string | null;
  protocol_alert?: string | null;
  [extra: string]: string | number | null | undefined;
};

export type ClientServicePreferencesFormValues = {
  preferred_professional_id: string;
  haircut_style: string;
  guard: string;
  beard_notes: string;
  post_care_notes: string;
  protocol_alert: string;
};

export const emptyServicePreferencesFormValues =
  (): ClientServicePreferencesFormValues => ({
  preferred_professional_id: "",
  haircut_style: "",
  guard: "",
  beard_notes: "",
  post_care_notes: "",
  protocol_alert: "",
});

export const mapPreferencesToFormValues = (
  prefs?: ClientServicePreferences | null
): ClientServicePreferencesFormValues => ({
  preferred_professional_id:
    prefs?.preferred_professional_id != null
      ? String(prefs.preferred_professional_id)
      : "",
  haircut_style: prefs?.haircut_style?.trim() ?? "",
  guard: prefs?.guard?.trim() ?? "",
  beard_notes: prefs?.beard_notes?.trim() ?? "",
  post_care_notes: prefs?.post_care_notes?.trim() ?? "",
  protocol_alert: prefs?.protocol_alert?.trim() ?? "",
});

export const buildServicePreferencesPayload = (
  values: ClientServicePreferencesFormValues
): ClientServicePreferences | null => {
  const payload: ClientServicePreferences = {};

  const professionalId = values.preferred_professional_id.trim();
  if (professionalId !== "") {
    const parsed = Number(professionalId);
    if (!Number.isNaN(parsed) && parsed > 0) {
      payload.preferred_professional_id = parsed;
    }
  }

  const assign = (key: keyof ClientServicePreferencesFormValues, max: number) => {
    if (key === "preferred_professional_id") {
      return;
    }
    const trimmed = values[key].trim();
    if (trimmed !== "") {
      payload[key] = trimmed.slice(0, max);
    }
  };

  assign("haircut_style", CLIENT_PREF_MAX_SHORT);
  assign("guard", CLIENT_PREF_MAX_SHORT);
  assign("beard_notes", CLIENT_PREF_MAX_NOTE);
  assign("post_care_notes", CLIENT_PREF_MAX_NOTE);
  assign("protocol_alert", CLIENT_PREF_MAX_NOTE);

  return Object.keys(payload).length === 0 ? null : payload;
};

export const hasClientServicePreferences = (
  prefs?: ClientServicePreferences | null
): boolean => {
  if (!prefs || typeof prefs !== "object") {
    return false;
  }

  if (
    prefs.preferred_professional_id != null &&
    Number(prefs.preferred_professional_id) > 0
  ) {
    return true;
  }

  return Object.entries(prefs).some(([key, value]) => {
    if (key === "preferred_professional_id") {
      return false;
    }
    return typeof value === "string" && value.trim() !== "";
  });
};

/** Short preview for chips / tooltips (excludes professional id). */
export const getServicePreferencesPreview = (
  prefs?: ClientServicePreferences | null,
  maxParts = 2,
  maxLength = 80
): string | null => {
  if (!hasClientServicePreferences(prefs)) {
    return null;
  }

  const parts: string[] = [];
  const order = [
    "haircut_style",
    "guard",
    "beard_notes",
    "post_care_notes",
    "protocol_alert",
  ] as const;

  for (const key of order) {
    const value = prefs?.[key];
    if (typeof value === "string" && value.trim()) {
      parts.push(value.trim());
    }
    if (parts.length >= maxParts) {
      break;
    }
  }

  if (parts.length < maxParts && prefs) {
    for (const [key, value] of Object.entries(prefs)) {
      if (
        key === "preferred_professional_id" ||
        order.includes(key as (typeof order)[number])
      ) {
        continue;
      }
      if (typeof value === "string" && value.trim()) {
        parts.push(value.trim());
      }
      if (parts.length >= maxParts) {
        break;
      }
    }
  }

  if (parts.length === 0) {
    return prefs?.preferred_professional_id ? "★" : null;
  }

  const joined = parts.join(" · ");
  if (joined.length <= maxLength) {
    return joined;
  }

  return `${joined.slice(0, maxLength).trimEnd()}…`;
};
